import type { AlertProps as LegacyAlertProps } from './Alert';
import type { AlertRole, AlertSeverity } from 'components/Alert/types';

/**
 * Maps the legacy MUI `Alert` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at ~70 call sites; retiring the flag means deleting it and letting call sites
 * speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS alert cannot express this one" and sends the caller back to MUI.
 * That is deliberate: a silent best-effort mapping would ship alerts that look or behave subtly
 * wrong with nothing to catch it at review time. Every `null` below is a design question that has
 * to be answered before those call sites can be refreshed.
 */
type DsProps = { severity: AlertSeverity; title?: LegacyAlertProps['title']; role?: AlertRole };

/**
 * Mapped by CELL, not by name.
 *
 * `white` is the wrapper's default and therefore what most of today's call sites render. DS 2.0
 * draws that cell as `neutral` — the page colour bounded by a border, with the icon in the text
 * colour — which `components/Alert/types.ts` calls out as the closest counterpart to `white`.
 *
 * `infoGray` and `infoDark` are theme-local severities (`MuiAlert.tsx` variants) with no DS cell:
 * one is a grey panel, the other a dark one, and the refreshed set has neither. Guessing at
 * `neutral` would flatten two visually distinct alerts into a third appearance.
 */
const SEVERITY: Partial<Record<NonNullable<LegacyAlertProps['variant']>, AlertSeverity>> = {
  white: 'neutral',
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

/** The only two the DS alert announces with; anything else is not a politeness it can honour. */
const isDsRole = (role: string): role is AlertRole => role === 'alert' || role === 'status';

/**
 * Cases the DS alert cannot express, all of which stay on MUI:
 *
 *   - `actions` — the refreshed set draws no actions row at all. Open question on HNS-339.
 *   - `dismissible` — the wrapper remembers the dismissal in `localStorage` and hides itself on the
 *     next visit. The DS alert deliberately keeps no such memory and never self-hides (see the
 *     `onDismiss` note in `components/Alert/types.ts`), so honouring this would change BEHAVIOUR,
 *     not just appearance.
 *   - `sx` — an Emotion prop with no effect on a Tailwind component. Accepting it would silently
 *     drop the styling rather than fail.
 *   - `Icon` — a caller-supplied MUI glyph. The DS alert owns its icon per severity, and the shape
 *     is load-bearing there: it is what makes severity legible without colour vision.
 *   - a bodyless alert — DS requires `children`, because a title with no body is a heading and the
 *     set draws no such cell.
 *   - a `role` other than `alert`/`status`.
 *
 * `onClose` is not consulted: the wrapper only wires it to a close button when `dismissible` is
 * set, and that case is already refused above.
 */
export const toDsProps = ({
  variant = 'white',
  title,
  description,
  children,
  actions = [],
  sx,
  Icon,
  dismissible,
  role,
}: LegacyAlertProps): DsProps | null => {
  if (sx || Icon || dismissible || actions.length) {
    return null;
  }

  // DS `children` is required, and it is the description slot.
  if (description === undefined && children === undefined) {
    return null;
  }

  if (role !== undefined && !isDsRole(role)) {
    return null;
  }

  const severity = SEVERITY[variant];

  if (!severity) {
    return null;
  }

  return { severity, title, role };
};
